import EntityAudits from "@/components/EntityAudits.vue";
import { LE } from "@/constants/enums";
import {
  EntityDocumentForm,
  EntityDocumentMultipleForm,
  EntityDocuments,
  EntityDocumentsChildList,
} from "@modernary/mui/components/document";
import { EntityNotes } from "@modernary/mui/components/note";
import { RouterView, type RouterOptions } from "vue-router";

const names = {
  complianceEvents: "ComplianceEvent.ComplianceEvents",
  list: "ComplianceEvent.ComplianceEventList",
  base: "ComplianceEvent.ComplianceEventBase",
  view: "ComplianceEvent.ComplianceEventView,",
  new: "ComplianceEvent.ComplianceEventNew",
  edit: "ComplianceEvent.ComplianceEventEdit",
  violationChildList: "ComplianceEvent.ComplianceEventViolationsChildList",
  violationNew: "ComplianceEvent.ComplianceEventViolationNew",
  violationSelect: "ComplianceEvent.ComplianceEventSelect",
  violationEdit: "ComplianceEvent.ComplianceEventViolationEdit",
  inspectionItemChildList: "ComplianceEvent.ComplianceEventInspectionItemChildList",
  inspectionItemNew: "ComplianceEvent.ComplianceEventInspectionItemEdit",
  inspectionItemEdit: "ComplianceEvent.ComplianceEventInspectionItemNew",
  notes: "ComplianceEvent.ComplianceEventNotes",
  noteEdit: "ComplianceEvent.ComplianceEventNoteEdit",
  noteNew: "ComplianceEvent.ComplianceEventNoteNew",
  documents: "ComplianceEvent.ComplianceEventDocuments",
  documentEdit: "ComplianceEvent.ComplianceEventDocumentEdit",
  documentNew: "ComplianceEvent.ComplianceEventDocumentNew",
  audit: "ComplianceEvent.ComplianceEventAudit",
};

export { names as complianceEventRouteNames };

const complianceEventRoutes: RouterOptions["routes"] = [
  {
    path: "/complianceEvents",
    name: names.complianceEvents,
    component: {
      render: () => <RouterView />,
    },
    redirect: { name: names.list },
    children: [
      {
        path: "list",
        name: names.list,
        meta: { title: "Compliance Events", lookupGroups: ["ComplianceEventType", "ComplianceEventStatus"] },
        component: () => import("@/views/complianceEvent/ComplianceEventList.vue"),
      },
      {
        path: "new/:facilityId(\\d+)?",
        component: () => import("@/views/complianceEvent/ComplianceEventBase.vue"),
        redirect: { name: names.new },
        props: true,
        children: [
          {
            path: "",
            name: names.new,
            meta: { title: "Compliance Event New", isNew: true },
            component: () => import("@/views/complianceEvent/ComplianceEventEdit.vue"),
          },
        ],
      },
      {
        path: ":id(\\d+)",
        name: names.base,
        component: () => import("@/views/complianceEvent/ComplianceEventBase.vue"),
        redirect: { name: names.view },
        children: [
          {
            path: "view",
            name: names.view,
            meta: { title: "Compliance Event View" },
            component: () => import("@/views/complianceEvent/ComplianceEventView.vue"),
          },
          {
            path: "edit",
            name: names.edit,
            meta: { title: "Compliance Event Edit", isForm: true },
            component: () => import("@/views/complianceEvent/ComplianceEventEdit.vue"),
          },
          {
            path: "violations",
            name: names.violationChildList,
            meta: { title: "Compliance Event Violations" },
            component: () => import("@/views/complianceEvent/ViolationChildList.vue"),
          },
          {
            path: "violations/select",
            name: names.violationSelect,
            meta: { title: "Compliance Event Violation New", isForm: true },
            component: () => import("@/views/complianceEvent/ViolationSelect.vue"),
          },
          {
            path: `violations/new/:typeMurn(${LE.ViolationType})`,
            name: names.violationNew,
            meta: { title: "Compliance Event Violation New", isForm: true },
            component: () => import("@/views/complianceEvent/ViolationEdit.vue"),
          },
          {
            path: "violations/:violationId(\\d+)",
            name: names.violationEdit,
            meta: { title: "Compliance Event Violation Edit", isForm: true },
            component: () => import("@/views/complianceEvent/ViolationEdit.vue"),
          },
          {
            path: "inspectionItems",
            name: names.inspectionItemChildList,
            meta: { title: "Compliance Event Inspection Items" },
            component: () => import("@/views/complianceEvent/InspectionItemChildList.vue"),
          },
          {
            path: "inspectionItems/new",
            name: names.inspectionItemNew,
            meta: { title: "Compliance Event Inspection Item New", isForm: true },
            component: () => import("@/views/complianceEvent/InspectionItemEdit.vue"),
          },
          {
            path: "inspectionItems/:inspectionItemId(\\d+)",
            name: names.inspectionItemEdit,
            meta: { title: "Compliance Event Inspection Item Edit", isForm: true },
            component: () => import("@/views/complianceEvent/InspectionItemEdit.vue"),
          },
          {
            path: "notes",
            name: names.notes,
            meta: { title: "Compliance Event Notes" },
            component: {
              render: () => (
                <EntityNotes idField="complianceEventId" routeNamePrefix="ComplianceEvent.ComplianceEventNote" />
              ),
            },
          },
          {
            path: "notes/:annotationId(\\d+)",
            name: names.noteEdit,
            meta: { title: "Compliance Event Note Edit", isForm: true },
            component: () => import("@/views/complianceEvent/ComplianceEventNote.vue"),
          },
          {
            path: "notes/new",
            name: names.noteNew,
            meta: { title: "Compliance Event Note New", isForm: true },
            component: () => import("@/views/complianceEvent/ComplianceEventNote.vue"),
          },
          {
            path: "documents",
            name: names.documents,
            meta: { title: "Compliance Event Documents" },
            component: {
              render: () => <EntityDocumentsChildList resourceTypeKey="complianceEvent" />,
            },
          },
          {
            path: "documents/:documentId(\\d+)",
            name: names.documentEdit,
            meta: { title: "Compliance Event Document Edit", isForm: true },
            component: {
              render: () => <EntityDocumentForm resourceTypeKey="complianceEvent" />,
            },
          },
          {
            path: "documents/new",
            name: names.documentNew,
            meta: { title: "Compliance Event Document New", isForm: true },
            component: {
              render: () => <EntityDocumentMultipleForm resourceTypeKey="complianceEvent" />,
            },
          },
          {
            path: "activity",
            name: names.audit,
            meta: { title: "ComplianceEvent Audit Activity" },
            component: {
              render: () => <EntityAudits entityName="ComplianceEvent" />,
            },
          },
        ],
      },
    ],
  },
];
export default complianceEventRoutes;
